import {useCallback, useEffect, useEffectEvent, useState} from 'react'

/**
 * Observe a single element with an IntersectionObserver.
 *
 * Returns a callback ref that should be attached to the target element.
 * When the element mounts, unmounts, or is swapped, the observer is
 * automatically re-created / cleaned up.
 *
 * @param callback      Called when the intersection observer fires
 * @param options       IntersectionObserver options (root, rootMargin, threshold)
 */
export function useIntersectionObserver(
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit,
): (node: Element | null) => void {
  const callbackRef = useEffectEvent(callback)
  const [element, setElement] = useState<Element | null>(null)

  const ref = useCallback((node: Element | null) => {
    setElement(node)
  }, [])

  useEffect(() => {
    if (!element) return

    const observer = new IntersectionObserver(entries => callbackRef(entries), options)
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, options])

  return ref
}
